import { Component, Inject, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Product, ProductCategory, Supplier } from 'app/entities';
import { Unit } from 'app/entities/unit';
import { guardDialogClose } from 'app/helpers/discard-guard';
import { oaDialogConfig } from 'app/helpers/oa-dialog';
import { Observable } from 'rxjs';
import { FormProductCategoryCreateOrEditComponent } from '../form-product-category-create-or-edit/form-product-category-create-or-edit.component';
import { FormProductCreateOrUpdateComponent } from '../form-product-create-or-update/form-product-create-or-update.component';
import { FormSupplierCreateOrEditComponent } from '../form-supplier-create-or-edit/form-supplier-create-or-edit.component';
import { FormUnitCreateOrEditComponent } from '../form-unit-create-or-edit/form-unit-create-or-edit.component';

export type CatalogFormKind = 'product' | 'unit' | 'category' | 'supplier';

export interface CatalogFormDialogData {
    kind: CatalogFormKind;
    /** Registro a editar; vazio = cadastro novo */
    entity?: Product | Unit | ProductCategory | Supplier | null;
    showStatusInput?: boolean;
}

export interface CatalogFormDialogResult<T = any> {
    action: 'created' | 'updated';
    value: T;
}

/** Interface comum dos formulários de cadastro usados aqui */
interface CatalogForm {
    form?: FormGroup;
    loading: boolean;
    store(): void;
    update(): void;
}

const NOUNS: Record<CatalogFormKind, string> = {
    product: 'produto',
    unit: 'unidade',
    category: 'categoria',
    supplier: 'fornecedor',
};

/** Abre o cadastro/edição. Emite o resultado, ou undefined se fechar sem salvar. */
export function openCatalogFormDialog<T = any>(
    dialog: MatDialog,
    data: CatalogFormDialogData,
): Observable<CatalogFormDialogResult<T> | undefined> {
    // Produto e fornecedor (formulários longos): tela cheia no celular.
    // Unidade e categoria (1–2 campos): bottom sheet, como "Registrar entrada/saída";
    // em tela cheia sobrava a tela quase toda em branco entre os campos e os botões.
    const long = data.kind === 'product' || data.kind === 'supplier';
    return dialog.open(CatalogFormDialogComponent, oaDialogConfig(long ? 'fullscreen' : 'sheet', {
        width: long ? '750px' : '480px',
        autoFocus: false,
        data,
    })).afterClosed();
}

// Cadastros de Produtos e Serviços (ORCA-1887). Eram mat-menu: no celular o rodapé
// ficava encostado na borda direita, o título solto por cima do app e o toque fora
// fechava sem confirmar. Mesmo padrão sheet do "Adicionar Cliente" (ORCA-1886).
@Component({
    selector: 'app-catalog-form-dialog',
    template: `
        <div class="oa-sheet">
            <header class="oa-sheet__header">
                <h2 class="oa-sheet__title" mat-dialog-title>{{ title }}</h2>
                <button type="button" class="oa-icon-btn" (click)="requestClose()" aria-label="Fechar">
                    <mat-icon>close</mat-icon>
                </button>
            </header>

            <div class="oa-sheet__body" [ngSwitch]="data.kind">
                <app-form-product-create-or-update *ngSwitchCase="'product'" #productForm [inDialog]="true"
                    [product]="$any(data.entity) || newProduct"
                    [showStatusInput]="data.showStatusInput !== false"
                    (onCreatedProduct)="done('created', $event)">
                </app-form-product-create-or-update>

                <app-form-unit-create-or-edit *ngSwitchCase="'unit'" #unitForm [inDialog]="true"
                    [unit]="$any(data.entity) || null"
                    (onCreatedUnit)="done('created', $event)" (onUpdatedUnit)="done('updated', $event)">
                </app-form-unit-create-or-edit>

                <app-form-product-category-create-or-edit *ngSwitchCase="'category'" #categoryForm [inDialog]="true"
                    [productCategory]="$any(data.entity) || null"
                    (onCreatedProduct)="done('created', $event)" (onUpdatedProduct)="done('updated', $event)">
                </app-form-product-category-create-or-edit>

                <app-form-supplier-create-or-edit *ngSwitchCase="'supplier'" #supplierForm [inDialog]="true"
                    [supplier]="$any(data.entity) || null"
                    (onCreatedSupplier)="done('created', $event)" (onUpdatedSupplier)="done('updated', $event)">
                </app-form-supplier-create-or-edit>
            </div>

            <footer class="oa-sheet__footer">
                <button type="button" class="oa-btn oa-btn--ghost" (click)="requestClose()">
                    {{ isEdit ? 'Cancelar' : 'Descartar' }}
                </button>
                <button type="button" class="oa-btn oa-btn--primary" [disabled]="!canSubmit"
                    [attr.aria-busy]="activeForm?.loading || null" (click)="submit()">
                    <span>{{ submitLabel }}</span>
                    <span *ngIf="activeForm?.loading" class="oa-spinner" aria-hidden="true"></span>
                </button>
            </footer>
        </div>
    `,
    styles: [`
        :host { display: flex; flex-direction: column; min-height: 0; }
    `],
})
export class CatalogFormDialogComponent {

    @ViewChild('productForm') productForm?: FormProductCreateOrUpdateComponent;
    @ViewChild('unitForm') unitForm?: FormUnitCreateOrEditComponent;
    @ViewChild('categoryForm') categoryForm?: FormProductCategoryCreateOrEditComponent;
    @ViewChild('supplierForm') supplierForm?: FormSupplierCreateOrEditComponent;

    readonly requestClose: () => void;
    readonly newProduct = new Product();

    constructor(
        public dialogRef: MatDialogRef<CatalogFormDialogComponent, CatalogFormDialogResult>,
        @Inject(MAT_DIALOG_DATA) public data: CatalogFormDialogData,
        dialog: MatDialog,
    ) {
        this.requestClose = guardDialogClose(dialogRef, dialog,
            () => !!this.activeForm?.form?.dirty,
            () => !!this.activeForm?.loading);
    }

    get isEdit(): boolean {
        return !!(this.data.entity as any)?.id;
    }

    get title(): string {
        const noun = NOUNS[this.data.kind];
        return this.isEdit ? `Editar ${noun}` : `Adicionar ${noun}`;
    }

    get submitLabel(): string {
        return this.isEdit ? 'Salvar' : `Criar ${NOUNS[this.data.kind]}`;
    }

    get activeForm(): CatalogForm | undefined {
        switch (this.data.kind) {
            case 'product': return this.productForm;
            case 'unit': return this.unitForm;
            case 'category': return this.categoryForm;
            case 'supplier': return this.supplierForm;
        }
    }

    get canSubmit(): boolean {
        const form = this.activeForm;
        return !!form?.form && form.form.valid && form.form.dirty && !form.loading;
    }

    submit(): void {
        if (!this.canSubmit) {
            return;
        }
        if (this.isEdit) {
            this.activeForm!.update();
        } else {
            this.activeForm!.store();
        }
    }

    done(action: 'created' | 'updated', value: any): void {
        this.dialogRef.close({ action, value });
    }
}
